import { useRef, useState } from "react";
import type { SeasonArchive, TeamDues } from "@averagejoesff/shared";
import { upsertDues } from "../../api/adminApi";
import { fetchDues } from "../../api/awards";
import { useAuth } from "../../auth/AuthProvider";
import { ResourceStatus } from "../../components/ResourceStatus";
import { useLeagueDocs } from "../../lib/LeagueDocsProvider";
import { useManagerNames } from "../../lib/useManagerNames";
import { useResource } from "../../lib/useResource";
import { DuesSummary } from "./DuesSummary";
import { DuesTeamList } from "./DuesTeamList";

interface DuesViewProps {
  archive: SeasonArchive;
}

/**
 * Season-scoped like `BlowoutView` (receives the already-loaded `archive` as
 * a prop rather than self-fetching a season), but — like `PrizePayoutsView`
 * — owns its own write path rather than routing through `AdminPanel`/"Edit
 * this season": toggling one team's status is a single-field, instantly
 * reversible action (click again), so it skips a separate edit-mode step or
 * form and just shows the toggle inline whenever logged in. No season
 * picker: dues are only ever tracked for the current season (see App.tsx —
 * this view always receives the current season's archive, never a
 * historical one).
 */
export function DuesView({ archive }: DuesViewProps) {
  const { isAuthenticated, idToken } = useAuth();
  const [dues, setDues] = useResource(() => fetchDues(archive.season), [archive.season]);
  const { payouts } = useLeagueDocs();
  const { labelByTeam } = useManagerNames(archive);
  const [error, setError] = useState<string | null>(null);
  // One save at a time. Each save sends every team's status, built from what
  // the page shows; a second click before the first save returned would send
  // the first team's old status and silently undo it. The ref guards against
  // two clicks inside one render; the state drives the disabled toggles.
  const saving = useRef(false);
  const [savingName, setSavingName] = useState<string | null>(null);

  // A team on this season's roster with no entry in `dues` yet (nothing set,
  // or a newly-added team) defaults to "due" — see `SeasonDues`'s doc comment.
  const statusByName = new Map(
    (dues.status === "ready" ? dues.data?.teams : undefined)?.map((t) => [t.name, t.status]) ?? [],
  );
  const rows = archive.teams.map((t) => ({
    name: t.name,
    status: statusByName.get(t.name) ?? ("due" as const),
    managerLabel: labelByTeam.get(t.name),
  }));
  const paidCount = rows.filter((r) => r.status === "paid").length;
  const dueCount = rows.length - paidCount;

  async function handleToggle(name: string) {
    if (!idToken || saving.current) return;
    saving.current = true;
    setSavingName(name);
    setError(null);

    const nextTeams: TeamDues[] = rows.map((r) => ({
      name: r.name,
      status: r.name === name ? (r.status === "due" ? "paid" : "due") : r.status,
    }));
    try {
      // Push the write response straight into state rather than re-fetching
      // — see useResource's doc comment on why a re-fetch this soon after a
      // write can hand back the pre-write cached copy.
      const updated = await upsertDues(archive.season, nextTeams, idToken);
      setDues(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      saving.current = false;
      setSavingName(null);
    }
  }

  return (
    <section className="feature" aria-labelledby="dues-heading">
      <div className="feature__intro">
        <div>
          <h2 id="dues-heading" className="feature__title">
            Dues Tracker
          </h2>
          <p className="feature__description">Buy-in status for the {archive.season} season.</p>
        </div>
      </div>

      {payouts.status === "ready" && payouts.data && dues.status === "ready" && (
        <DuesSummary
          buyInPerTeam={payouts.data.buyInPerTeam}
          paidCount={paidCount}
          dueCount={dueCount}
        />
      )}

      <ResourceStatus resource={dues} loading="Loading dues…" error="Could not load dues" />
      {dues.status === "ready" && (
        <DuesTeamList
          teams={rows}
          isAuthenticated={isAuthenticated && idToken !== null}
          savingName={savingName}
          onToggle={handleToggle}
        />
      )}

      {error && <p className="admin-panel__status admin-panel__status--error">{error}</p>}
    </section>
  );
}
